export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// A request that never settles leaves whatever page made it stuck on its loading state
// forever, with nothing on screen to explain why. Fail loudly instead.
const REQUEST_TIMEOUT_MS = 25_000;

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'include',
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
      signal: controller.signal,
      ...options,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('The server took too long to respond. Please try again.', 504, 'REQUEST_TIMEOUT');
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message = (isJson && (body as { error?: string })?.error) || 'Something went wrong. Please try again.';
    const code = isJson ? (body as { code?: string })?.code : undefined;
    const details = isJson ? (body as { details?: unknown })?.details : undefined;
    throw new ApiError(message, response.status, code, details);
  }

  return body as T;
}

/**
 * Sends a file as the raw request body (Content-Type is the file's type, X-File-Name its name).
 * Allowed longer than an ordinary request, since a resume can take a while on a slow connection.
 */
async function uploadFile<T>(path: string, file: File): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 120_000);
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name) },
      body: file,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new ApiError('The upload took too long. Please try again.', 504, 'REQUEST_TIMEOUT');
    throw error;
  } finally {
    clearTimeout(timeout);
  }
  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await response.json() : await response.text();
  if (!response.ok) {
    const message = (isJson && (body as { error?: string })?.error) || (response.status === 413 ? 'That file is too large. The limit is 10 MB.' : 'Something went wrong. Please try again.');
    throw new ApiError(message, response.status, isJson ? (body as { code?: string })?.code : undefined);
  }
  return body as T;
}

export const api = {
  upload: uploadFile,
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined }),
};
